import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || (
    process.env.NODE_ENV === 'production'
      ? 'https://api.mhdfmomentum.com/api'
      : 'http://localhost:5000/api'
  ),
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
    return Promise.reject(error);
  }
);

export default api;

// API service functions
export const authService = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  verifyEmail: (token) => api.get(`/auth/verify/${token}`),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  forgotUsername: (email) => api.post('/auth/forgot-username', { email }),
  resetPassword: (payload) => api.post('/auth/reset-password', payload),
  resetPasswordVerified: (payload) => api.post('/auth/reset-password-verified', payload)
};

export const userService = {
  getProfile: (userId) => api.get(`/users/${userId}`),
  updateProfile: (userId, data) => api.put(`/users/${userId}`, data),
  acknowledgeEnrollment: (userId) => api.post(`/users/${userId}/enrollment-acknowledgment`),
  changePassword: (userId, passwords) => api.put(`/users/${userId}/password`, passwords),
  inviteClinician: (userId, clinicianEmail, clinicianPhone = '') =>
    api.post(`/users/${userId}/clinician-invite`, { clinicianEmail, clinicianPhone }),
  getConsent: (userId) => api.get(`/users/${userId}/consent`),
  updateConsent: (userId, consent) => api.put(`/users/${userId}/consent`, consent),
  assignClinician: (userId, clinicianId) => api.put(`/users/${userId}/clinician`, { clinicianId }),
  requestClinicianUnassign: (userId) => api.post(`/users/${userId}/clinician/unassign-request`),
  getClinicians: (availableOnly = true) => api.get('/users/clinicians', { params: { available: availableOnly } }),
  graduate: (userId) => api.post(`/users/${userId}/graduate`),
  continueProgram: (userId) => api.post(`/users/${userId}/continue`),
  unenroll: (userId) => api.post(`/users/${userId}/unenroll`),
  deleteAccount: (userId) => api.delete(`/users/${userId}`),
  getCertificate: (certificateId) => api.get(`/users/certificate/${certificateId}`),
  downloadDataExport: (userId) => api.get(`/users/export/download`),
  getExportPreview: (userId) => api.get(`/users/export/preview`),
  getActivities: (category, difficulty) => {
    const params = {};
    if (category) params.category = category;
    if (difficulty) params.difficulty = difficulty;
    return api.get('/activities', { params });
  },
  logActivityCompletion: (activityId, data) => api.post(`/activities/${activityId}/complete`, data),
  getWeeklyActivityStats: (weekStart) => api.get(`/activities/stats/weekly/${weekStart}`),
  getNotificationPreferences: () => api.get('/notifications/email/preferences'),
  updateNotificationPreferences: (preferences) => api.put('/notifications/email/preferences', preferences),
  sendTestNotification: (channel = 'email') => api.post('/notifications/email/test', { channel }),
  getPendingNotifications: () => api.get('/notifications/email/pending'),
  dismissNotification: (notificationId) => api.patch(`/notifications/email/${notificationId}/dismiss`)
};

export const dailyContentService = {
  getDay: (day) => api.get(`/daily-content/${day}`),
  getAdaptiveDay: (day) => api.get(`/daily-content/adaptive/${day}`),
  getPhase: (phase) => api.get(`/daily-content/phase/${phase}`)
};

export const journalService = {
  createEntry: (userId, entryData) => api.post(`/journal/${userId}`, entryData),
  getEntry: (userId, day) => api.get(`/journal/${userId}/${day}`),
  getAllEntries: (userId, startDay, endDay) => {
    const params = startDay && endDay ? { startDay, endDay } : {};
    return api.get(`/journal/${userId}`, { params });
  },
  searchEntries: (userId, params) => api.get(`/journal/${userId}/search`, { params }),
  getRetrospective: (userId, currentDay) => api.get(`/journal/${userId}/retrospective/${currentDay}`),
  togglePrivacy: (userId, entryId, isPrivate) => api.patch(`/journal/${userId}/${entryId}/privacy`, { isPrivate }),
  updateSharing: (userId, shareJournalWithClinician) => api.put(`/journal/${userId}/sharing`, { shareJournalWithClinician })
};

export const progressService = {
  getProgress: (userId) => api.get(`/progress/${userId}`),
  getTrends: (userId, days) => api.get(`/progress/${userId}/trends`, { params: { days } }),
  getSafetyFlags: (userId) => api.get(`/progress/${userId}/safety-flags`),
  addAssessment: (userId, assessment) => api.post(`/progress/${userId}/assessment`, assessment),
  updatePlaybook: (userId, playbook) => api.put(`/progress/${userId}/playbook`, playbook),
  getBadges: (userId) => api.get(`/progress/${userId}/badges`)
};

export const weeklyReflectionService = {
  getReflection: (userId, weekNumber) => api.get(`/weekly-reflections/${userId}/${weekNumber}`),
  saveReflection: (userId, payload) => api.post(`/weekly-reflections/${userId}`, payload)
};

export const clinicianService = {
  getClients: () => api.get('/clinician/clients'),
  getClientOverview: (clientId) => api.get(`/clinician/clients/${clientId}/overview`),
  getClientConsent: (clientId) => api.get(`/clinician/clients/${clientId}/consent`),
  getClientProfile: (clientId) => api.get(`/clinician/clients/${clientId}/profile`),
  getDashboardStats: () => api.get('/clinician/dashboard/stats'),
  getEncouragementPrompts: () => api.get('/clinician/encouragement/prompts'),
  inviteClient: (payload) => api.post('/clinician/invitations', payload),
  resendInvite: (clientId, generateNewPassword = true) => api.post(`/clinician/clients/${clientId}/resend-invite`, { generateNewPassword }),
  acceptInvite: (notificationId) => api.post(`/clinician/invitations/${notificationId}/accept`),
  removeInvite: (notificationId) => api.post(`/clinician/invitations/${notificationId}/remove`),
  suspendClient: (clientId) => api.post(`/clinician/clients/${clientId}/suspend`),
  reactivateClient: (clientId) => api.post(`/clinician/clients/${clientId}/reactivate`),
  graduateClient: (clientId) => api.post(`/clinician/clients/${clientId}/graduate`),
  approveUnassign: (clientId) => api.post(`/clinician/clients/${clientId}/unassign-approve`),
  unassignSelf: (clientId) => api.patch(`/clinician/clients/${clientId}/unassign-self`),
  deleteClient: (clientId) => api.delete(`/clinician/clients/${clientId}`),
  updateClientContactInfo: (clientId, data) => api.patch(`/clinician/clients/${clientId}/contact-info`, data),

  // Clinical alerts
  getAlerts: (status = 'active', severity = null, limit = 20, days = 7) => {
    return api.get('/clinician/alerts', {
      params: {
        status: status || undefined,
        severity: severity || undefined,
        limit,
        days
      }
    });
  },
  getAlertDetail: (alertId) => api.get(`/clinician/alerts/${alertId}`),
  updateAlert: (alertId, payload) => api.patch(`/clinician/alerts/${alertId}`, payload),
  getClientAlerts: (clientId) => api.get(`/clinician/clients/${clientId}/alerts`),

  // Independent invitations
  sendInvitation: (payload) => api.post('/clinician/invitations/send', payload),
  getInvitations: (params = {}) => api.get('/clinician/invitations', { params }),
  cancelInvitation: (invitationId) => api.delete(`/clinician/invitations/${invitationId}`)
};

export const coachService = {
  getParticipants: () => api.get('/coach/participants'),
  getParticipantGoals: (participantId) => api.get(`/coach/participants/${participantId}/goals`),
  createGoal: (participantId, payload) => api.post(`/coach/participants/${participantId}/goals`, payload),
  updateGoal: (goalId, payload) => api.patch(`/coach/goals/${goalId}`, payload),
  getReferralCode: () => api.post('/coach/referral-code'),

  // Independent invitations
  sendInvitation: (payload) => api.post('/coach/invitations/send', payload),
  getInvitations: (params = {}) => api.get('/coach/invitations', { params }),
  cancelInvitation: (invitationId) => api.delete(`/coach/invitations/${invitationId}`)
};

export const encouragementService = {
  getTemplates: () => api.get('/encouragement/templates'),
  getTemplatesByCategory: (category) => api.get(`/encouragement/templates/${category}`),
  checkEligibility: (clientId) => api.post('/encouragement/check-eligibility', { clientId }),
  sendMessage: (payload) => api.post('/encouragement/send', payload),
  getSent: (params = {}) => api.get('/encouragement/sent', { params }),
  getReceived: (params = {}) => api.get('/encouragement/received', { params }),
  markRead: (messageId) => api.put(`/encouragement/${messageId}/read`)
};

export const notificationService = {
  getNotifications: () => api.get('/notifications'),
  refreshNotifications: () => api.post('/notifications/refresh'),
  markRead: (notificationId) => api.patch(`/notifications/${notificationId}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  subscribePush: (subscription) => api.post('/push/subscribe', { subscription }),
  unsubscribePush: (endpoint) => api.post('/push/unsubscribe', { endpoint }),
  sendPushTest: () => api.post('/push/test'),
  getSMSPreferences: () => api.get('/notifications/sms/preferences'),
  updateSMSPreferences: (preferences) => api.put('/notifications/sms/preferences', preferences),
  validatePhone: (phone) => api.post('/notifications/sms/validate-phone', { phone }),
  sendSMSTest: () => api.post('/notifications/sms/test')
};

export const safetyPlanService = {
  getSafetyPlan: (userId) => api.get(`/safety-plan/${userId}`),
  saveSafetyPlan: (userId, payload) => api.put(`/safety-plan/${userId}`, payload)
};

export const phaseTransitionService = {
  getPending: (userId) => api.get(`/phase-transitions/${userId}/pending`),
  getAllTransitions: (userId) => api.get(`/phase-transitions/${userId}`),
  getTransition: (userId, phaseCompleted) => api.get(`/phase-transitions/${userId}/${phaseCompleted}`),
  saveReflection: (userId, payload) => api.post(`/phase-transitions/${userId}`, payload),
  markViewed: (userId, phaseCompleted) => api.patch(`/phase-transitions/${userId}/${phaseCompleted}/viewed`)
};

export const lapseService = {
  reportLapse: (userId, payload) => api.post(`/lapse/${userId}`, payload),
  getLapseReports: (userId, limit = 10) => api.get(`/lapse/${userId}`, { params: { limit } }),
  getRecentLapse: (userId) => api.get(`/lapse/${userId}/recent`),
  getLapseReport: (userId, lapseId) => api.get(`/lapse/${userId}/${lapseId}`),
  addClinicianNotes: (userId, lapseId, notes) => api.patch(`/lapse/${userId}/${lapseId}/clinician-notes`, { notes }),
  markReengaged: (userId, lapseId) => api.patch(`/lapse/${userId}/${lapseId}/reengaged`),
  getLapseStats: (userId) => api.get(`/lapse/${userId}/stats`)
};

export const adminService = {
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  getUserById: (userId) => api.get(`/admin/users/${userId}`),
  getAuditLogs: (params = {}) => api.get('/admin/audit-logs', { params }),
  deleteAuditLog: (logId) => api.delete(`/admin/audit-logs/${logId}`),
  deleteAuditLogs: (params = {}) => api.delete('/admin/audit-logs', { params }),
  createClient: (data) => api.post('/admin/clients', data),
  createClinician: (data) => api.post('/admin/clinicians', data),
  createClergy: (data) => api.post('/admin/clergy', data),
  createAdmin: (data) => api.post('/admin/admins', data),
  updateUserAccess: (userId, data) => api.patch(`/admin/users/${userId}/access`, data),
  updateUserProfile: (userId, data) => api.patch(`/admin/users/${userId}/profile`, data),
  updateUserSubscription: (userId, data) => api.patch(`/admin/users/${userId}/subscription`, data),
  sendPasswordReset: (userId) => api.post(`/admin/users/${userId}/reset-password`),
  blockUser: (userId) => api.post(`/admin/users/${userId}/block`),
  unblockUser: (userId) => api.post(`/admin/users/${userId}/unblock`),
  suspendUser: (userId) => api.post(`/admin/users/${userId}/suspend`),
  reactivateUser: (userId) => api.post(`/admin/users/${userId}/reactivate`),
  verifyUser: (userId) => api.post(`/admin/users/${userId}/verify`),
  unverifyUser: (userId) => api.post(`/admin/users/${userId}/unverify`),
  deleteUser: (userId, hard = false) => api.delete(`/admin/users/${userId}`, { params: { hard } }),
  restoreUser: (userId, payload = {}) => api.patch(`/admin/users/${userId}/restore`, payload),
  graduateUser: (userId) => api.post(`/admin/users/${userId}/graduate`),
  grantComplimentaryAccess: (userId, payload) => api.post(`/admin/users/${userId}/grant-complimentary`, payload),
  previewEmailRecipients: (filters) => api.post('/admin/emails/preview', filters),
  sendEmail: (payload) => api.post('/admin/emails/send', payload),
  // Invitation management
  sendInvitation: (payload) => api.post('/invitations/admin/send', payload),
  getInvitations: (params = {}) => api.get('/invitations/admin/all', { params }),
  cancelInvitation: (invitationId) => api.delete(`/invitations/admin/${invitationId}`)
};

export const faithService = {
  // Faith content endpoints
  getContentForDay: (day) => api.get(`/faith/content/${day}`),
  getTodayContent: () => api.get('/faith/content/today'),
  getPhaseContent: (phase) => api.get(`/faith/content/phase/${phase}`),
  createContent: (contentData) => api.post('/faith/content', contentData),
  updateContent: (contentId, contentData) => api.put(`/faith/content/${contentId}`, contentData),
  bulkImportContent: (items) => api.post('/faith/content/bulk', { items }),
  seedContent: (reset = false) => api.post('/faith/seed', { reset }),

  // Faith journal endpoints
  createJournalEntry: (userId, entryData) => api.post(`/faith/journal/${userId}`, entryData),
  getJournalEntry: (userId, day, options = {}) => {
    const { optional = true } = options;
    return api.get(`/faith/journal/${userId}/${day}`, {
      params: optional ? { optional: 1 } : undefined
    });
  },
  getAllJournalEntries: (userId) => api.get(`/faith/journal/${userId}`),

  // User faith preferences
  updateFaithPreferences: (userId, preferences) => api.put(`/users/${userId}/faith-preferences`, preferences)
};

export const homeworkService = {
  // Client endpoints
  getAssignedHomework: (status = 'active') => api.get('/homework/assigned', { params: { status } }),
  downloadHomework: (assignmentId) => api.get(`/homework/${assignmentId}/download`, { responseType: 'blob' }),
  emailHomework: (assignmentId) => api.post(`/homework/${assignmentId}/email`),
  getInteractiveForm: (assignmentId) => api.get(`/homework/${assignmentId}/form`),
  saveInteractiveForm: (assignmentId, responses) => api.patch(`/homework/${assignmentId}/form`, { responses }),
  submitInteractiveForm: (assignmentId, responses) => api.post(`/homework/${assignmentId}/submit`, { responses }),
  exportInteractiveFormPdf: (assignmentId) => api.get(`/homework/${assignmentId}/form/export`, { responseType: 'blob' }),
  getClinicianInteractiveStatus: (clientId) => api.get(`/homework/clients/${clientId}/interactive-status`),

  // Clinician endpoints
  getTemplates: (setId = null) => api.get('/homework/templates', { params: { setId } }),
  assignHomework: (payload) => api.post('/homework/assign', payload),
  getClientHomework: (clientId, status = 'all') => api.get(`/homework/clients/${clientId}`, { params: { status } }),
  archiveAssignment: (assignmentId) => api.post(`/homework/${assignmentId}/archive`),
  deleteAssignment: (assignmentId) => api.delete(`/homework/${assignmentId}`),
  refreshAssignment: (assignmentId, payload = {}) => api.post(`/homework/${assignmentId}/refresh`, payload),
  alertAssignment: (assignmentId) => api.post(`/homework/${assignmentId}/alert`),
  resendAssignment: (assignmentId) => api.post(`/homework/${assignmentId}/resend`)
};

// ── Wellness-Area Homework System ──
export const wellnessAreaService = {
  list: () => api.get('/wellness-areas'),
  get: (slug) => api.get(`/wellness-areas/${slug}`)
};

export const wellnessHomeworkService = {
  // Library
  getLibrary: (params = {}) => api.get('/homework/library', { params }),
  getLibraryItem: (id) => api.get(`/homework/library/${id}`),

  // Assignments — self
  assignSelf: (payload) => api.post('/homework/assignments', payload),
  // Assignments — clinician
  assignForClient: (payload) => api.post('/homework/assignments/clinician', payload),
  // Reads
  getUserAssignments: (userId) => api.get(`/homework/assignments/${userId}`),
  getActiveAssignments: (userId) => api.get(`/homework/assignments/${userId}/active`),
  downloadAssignmentPdf: (id) => api.get(`/homework/assignments/${id}/download`, { responseType: 'blob' }),
  emailAssignmentPdf: (id) => api.post(`/homework/assignments/${id}/email`),
  // Transitions
  startAssignment: (id) => api.patch(`/homework/assignments/${id}/start`),
  completeAssignment: (id, userResponse) => api.patch(`/homework/assignments/${id}/complete`, { userResponse }),
  skipAssignment: (id, reason) => api.patch(`/homework/assignments/${id}/skip`, { reason }),
  getInteractiveForm: (id) => api.get(`/homework/assignments/${id}/form`),
  saveInteractiveForm: (id, responses) => api.patch(`/homework/assignments/${id}/form`, { responses }),
  submitInteractiveForm: (id, responses) => api.post(`/homework/assignments/${id}/submit`, { responses }),
  exportInteractiveFormPdf: (id) => api.get(`/homework/assignments/${id}/form/export`, { responseType: 'blob' }),
};

export const userWellnessService = {
  getClinicianAreas: (userId) => api.get(`/users/${userId}/clinician-wellness-areas`),
  updateAreas: (userId, wellnessAreas) => api.patch(`/users/${userId}/wellness-areas`, { wellnessAreas }),
  updatePreferences: (userId, preferences) => api.patch(`/users/${userId}/homework-preferences`, preferences)
};
// ── Stripe Billing ──
export const stripeService = {
  // Create a Checkout Session and redirect to Stripe
  createCheckoutSession: (planKey, billingCycle, couponCode = null) =>
    api.post('/stripe/create-checkout-session', {
      planKey,
      billingCycle,
      couponCode: couponCode || undefined,
    }),

  // Open the Stripe Customer Portal for billing management
  openCustomerPortal: () => api.post('/stripe/customer-portal'),

  // Get current subscription status
  getSubscriptionStatus: () => api.get('/stripe/subscription-status'),

  // Get available plans and pricing (public — no auth required)
  getAvailablePlans: () => api.get('/stripe/plans'),
};